import React from 'react';
import {
  Alert,
  alpha,
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import { useCurrency } from '../../ui/BootstrapProvider';
import { usePageBanner } from '../../ui/PageBannerProvider';
import { formatL2CValueWithSuffix } from './utils/formatNumbers';
import ChainView, { ChainHealthSummary, StageDetailPanel } from './components/ChainView';
import ExecutiveSummary from './components/ExecutiveSummary';
import LeadToCashToolbar from './components/LeadToCashToolbar';
import LeadToCashChatPanel from './components/LeadToCashChatPanel';
import DialOpPeriodContext from '../dialing-opportunity/components/DialOpPeriodContext';
import {
  type CustomRange,
  defaultDialOpRange,
  resolveDialOpComparePeriod,
  snapshotMatchesRange,
} from '../dialing-opportunity/components/PeriodComparePicker';
import { useLeadToCashChat } from './hooks/useLeadToCashChat';
import { buildL2CSurfaceContext } from './utils/buildL2CSurfaceContext';
import {
  isPanelBreakdownCurrent,
  isStagePrimaryMetricCurrency,
  normalizeSnapshotPanels,
  paginateStagePanel,
  panelHasBreakdownStore,
  updateStagePanel,
} from './utils/paginateStagePanel';
import { ChainSkeleton, StageDetailSkeleton } from './components/LeadToCashLoadingSkeleton';
import {
  fetchLeadToCashChain,
  fetchLeadToCashMetricBreakdown,
  fetchLeadToCashStageDetail,
  rerunLeadToCashChain,
  type L2CChainQuery,
} from '../../services/leadToCashService';
import type {
  L2CChainSnapshot,
  L2CDriver,
  L2CMetricBreakdown,
  L2CMover,
  L2CPeriodGrain,
  L2CStage,
} from '../../types/leadToCash';

const L2C_SELECTED_STAGE_KEY = 'klaris_l2c_selected_stage';
const L2C_CHAT_WIDTH_KEY = 'klaris_l2c_chat_width';
const L2C_CHAT_WIDTH_DEFAULT = 420;
const L2C_CHAT_WIDTH_MIN = 320;
const L2C_CHAT_WIDTH_MAX = 720;

const LEGACY_STAGE_ID_MAP: Record<string, string> = {
  pre_mql: 'spend',
  gross_leads: 'mql',
  call_connect: 'connect',
  sqo: 'cas',
  decision: 'approved',
  commission: 'cash',
};

function normalizeStageId(stageId: string): string {
  return LEGACY_STAGE_ID_MAP[stageId] ?? stageId;
}

function daySpan(start: string, end: string): number {
  const s = Date.parse(`${start}T00:00:00Z`);
  const e = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(s) || Number.isNaN(e)) return 7;
  return Math.round((e - s) / 86_400_000) + 1;
}

/**
 * The custom picker lets the user select any date span, but the backend/UI still
 * need a grain label ("This wk", "This day", ...) for period headers. Pick the
 * closest match from the actual selected span instead of assuming a week — a
 * single day, a month, or a year should be labeled as such, not "wk".
 */
function deriveGrainFromRange(range: CustomRange | null): L2CPeriodGrain {
  if (!range?.currentStart || !range?.currentEnd) return 'week';
  const span = daySpan(range.currentStart, range.currentEnd);
  if (span <= 1) return 'day';
  if (span <= 13) return 'week';
  if (span <= 45) return 'month';
  return 'year';
}

function rangeToQuery(range: CustomRange | null, refresh = false): L2CChainQuery {
  const query: L2CChainQuery = { grain: deriveGrainFromRange(range), preset: 'custom', refresh };
  if (range?.currentStart && range?.currentEnd) {
    query.currentStart = range.currentStart;
    query.currentEnd = range.currentEnd;
    if (range.compareStart && range.compareEnd) {
      query.compareStart = range.compareStart;
      query.compareEnd = range.compareEnd;
    }
  }
  return query;
}

function pickDefaultStage(stages: L2CStage[]): string | null {
  const sorted = [...stages]
    .filter((s) => !s.data_unavailable)
    .sort((a, b) => a.order - b.order);
  return (
    sorted.find((s) => s.status === 'red')?.id
    ?? sorted.find((s) => s.status === 'yellow')?.id
    ?? sorted[0]?.id
    ?? null
  );
}

/** Prefer last selected stage, then any stage with a cached panel, then health heuristic. */
function pickInitialStageId(stages: L2CStage[]): string | null {
  const available = stages.filter((s) => !s.data_unavailable);
  if (!available.length) return null;

  try {
    const stored = sessionStorage.getItem(L2C_SELECTED_STAGE_KEY);
    if (stored) {
      const normalized = normalizeStageId(stored);
      if (available.some((s) => s.id === normalized)) {
        return normalized;
      }
    }
  } catch {
    // sessionStorage unavailable
  }

  const sorted = [...available].sort((a, b) => a.order - b.order);
  const withPanel = sorted.filter((s) => s.panel);
  if (withPanel.length) {
    return (
      withPanel.find((s) => s.status === 'red')?.id
      ?? withPanel.find((s) => s.status === 'yellow')?.id
      ?? withPanel[0].id
    );
  }

  return pickDefaultStage(stages);
}

function metricBreakdownCacheKey(stageId: string, metricKey: string, driverId: string): string {
  return `${stageId}:${metricKey}:${driverId}`;
}

function isStaleZeroConversionBreakdown(
  stage: L2CStage | undefined,
  metricKey: string,
  movers: L2CMover[] | undefined,
): boolean {
  if (!stage || !movers?.length) return false;
  const metric = [...stage.headline_metrics, ...(stage.supporting_metrics ?? [])]
    .find((m) => m.key === metricKey);
  if (metric?.format !== 'percent') return false;
  return movers.every((m) => m.this_period === 0 && m.prior_period === 0);
}

function readPanelMetricBreakdown(
  stage: L2CStage | undefined,
  metricKey: string,
  driverId: string,
): L2CMetricBreakdown | null {
  const store = stage?.panel?.metric_breakdowns?.[`${metricKey}:${driverId}`];
  if (!store?.movers) return null;
  if (isStaleZeroConversionBreakdown(stage, metricKey, store.movers)) return null;
  const narrative = stage?.panel?.metric_narratives?.[metricKey];
  return {
    stage_id: stage!.id,
    metric_key: metricKey,
    driver_id: driverId,
    movers: store.movers,
    total_count: store.total_count,
    from_cache: true,
    metric_summary: narrative?.summary ?? null,
    metric_summary_source: narrative?.source ?? null,
  };
}

function stageHasPanel(stage: L2CStage | undefined): boolean {
  return isPanelBreakdownCurrent(stage?.panel);
}

function buildStageDetailPayload(stage: L2CStage, snapshot: L2CChainSnapshot) {
  const currentPeriod = snapshot.current_period ?? snapshot.current_week;
  const priorPeriod = snapshot.prior_period ?? snapshot.prior_week;
  if (!currentPeriod?.start || !priorPeriod?.start) return null;

  return {
    grain: snapshot.period_grain ?? 'week',
    preset: snapshot.comparison_preset ?? 'custom',
    current_period: currentPeriod,
    prior_period: priorPeriod,
    headline_metrics: stage.headline_metrics,
    comparison_label: snapshot.comparison_label,
    comparison_mode: snapshot.comparison_mode,
    capacity: snapshot.capacity ?? null,
  };
}

export default function LeadToCashMonitorPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const leadToCashFeature = useFeature('lead_to_cash_monitor');
  const { symbol: currencySymbol } = useCurrency();
  const [custom, setCustom] = React.useState<CustomRange | null>(() => defaultDialOpRange());
  const [snapshot, setSnapshot] = React.useState<L2CChainSnapshot | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = React.useState<string | null>(null);
  const [detailLoadingStageId, setDetailLoadingStageId] = React.useState<string | null>(null);
  const [narrativeLoadingStageId, setNarrativeLoadingStageId] = React.useState<string | null>(null);
  const [detailRetryNonce, setDetailRetryNonce] = React.useState(0);
  const [activeBreakdownMetricKey, setActiveBreakdownMetricKey] = React.useState<string | null>(null);
  const [metricBreakdown, setMetricBreakdown] = React.useState<L2CMetricBreakdown | null>(null);
  const [metricBreakdownLoading, setMetricBreakdownLoading] = React.useState(false);
  const [metricBreakdownDriverId, setMetricBreakdownDriverId] = React.useState<string | null>(null);
  const [headlineBreakdownDriverId, setHeadlineBreakdownDriverId] = React.useState<string | null>(null);
  const [breakdownDriverLoading, setBreakdownDriverLoading] = React.useState(false);
  const [chatOpen, setChatOpen] = React.useState(false);
  const [chatWidth, setChatWidth] = React.useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(L2C_CHAT_WIDTH_KEY);
      const v = raw ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(v)) return Math.min(L2C_CHAT_WIDTH_MAX, Math.max(L2C_CHAT_WIDTH_MIN, v));
    } catch {
      // ignore
    }
    return L2C_CHAT_WIDTH_DEFAULT;
  });
  const dragStateRef = React.useRef<{ startX: number; startWidth: number } | null>(null);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(L2C_CHAT_WIDTH_KEY, String(chatWidth));
    } catch {
      // ignore
    }
  }, [chatWidth]);

  const onResizeStart = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startWidth: chatWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [chatWidth]);

  const onResizeMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const ds = dragStateRef.current;
    if (!ds) return;
    const dx = e.clientX - ds.startX;
    const next = Math.min(
      L2C_CHAT_WIDTH_MAX,
      Math.max(L2C_CHAT_WIDTH_MIN, ds.startWidth - dx),
    );
    setChatWidth(next);
  }, []);

  const onResizeEnd = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const onResizeDoubleClick = React.useCallback(() => {
    setChatWidth(L2C_CHAT_WIDTH_DEFAULT);
  }, []);

  const applySnapshot = React.useCallback((data: L2CChainSnapshot) => {
    setSnapshot(normalizeSnapshotPanels(data, currencySymbol));
    setSelectedStageId((prev) => {
      if (prev && data.stages.some((s) => s.id === prev && !s.data_unavailable)) {
        return prev;
      }
      return pickInitialStageId(data.stages);
    });
  }, [currencySymbol]);

  const loadGeneration = React.useRef(0);
  const detailInFlight = React.useRef<string | null>(null);
  const metricBreakdownCache = React.useRef<Map<string, L2CMetricBreakdown>>(new Map());
  // Guards against out-of-order responses when the user switches metrics/drivers
  // faster than a request (e.g. a first-time LLM metric summary) resolves — only
  // the response matching the *currently selected* key is ever applied to state.
  const metricBreakdownActiveKey = React.useRef<string | null>(null);
  // Dedupes concurrent requests for the same metric+driver so switching away and
  // back while the first request is still in flight doesn't fire (and persist) a
  // second, independent metric-summary generation.
  const metricBreakdownInFlight = React.useRef<Map<string, Promise<L2CMetricBreakdown>>>(new Map());
  const narrativePollTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const narrativePollAttempts = React.useRef(0);
  const snapshotRef = React.useRef<L2CChainSnapshot | null>(null);
  const surfaceContextRef = React.useRef<Record<string, unknown> | null>(null);

  React.useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const clearNarrativePoll = React.useCallback(() => {
    if (narrativePollTimeout.current) {
      clearTimeout(narrativePollTimeout.current);
      narrativePollTimeout.current = null;
    }
    narrativePollAttempts.current = 0;
  }, []);

  const pollStageNarrative = React.useCallback((stageId: string) => {
    clearNarrativePoll();
    setNarrativeLoadingStageId(stageId);

    const attempt = async () => {
      narrativePollAttempts.current += 1;
      if (narrativePollAttempts.current > 30) {
        setNarrativeLoadingStageId((current) => (current === stageId ? null : current));
        return;
      }

      const snapshotData = snapshotRef.current;
      const stage = snapshotData?.stages.find((s) => s.id === stageId);
      if (!snapshotData || !stage) {
        narrativePollTimeout.current = setTimeout(attempt, 1500);
        return;
      }

      const payload = buildStageDetailPayload(stage, snapshotData);
      if (!payload) {
        setNarrativeLoadingStageId((current) => (current === stageId ? null : current));
        return;
      }

      try {
        const detail = await fetchLeadToCashStageDetail(stageId, payload);
        if (!detail.panel) {
          narrativePollTimeout.current = setTimeout(attempt, 1500);
          return;
        }

        setSnapshot((prev) => {
          if (!prev) return prev;
          return updateStagePanel(prev, stageId, detail.panel!, currencySymbol);
        });

        if (detail.narrative_pending || detail.panel.narrative_pending) {
          narrativePollTimeout.current = setTimeout(attempt, 1500);
          return;
        }

        setNarrativeLoadingStageId((current) => (current === stageId ? null : current));
      } catch {
        narrativePollTimeout.current = setTimeout(attempt, 1500);
      }
    };

    void attempt();
  }, [clearNarrativePoll, currencySymbol]);

  const loadMetricBreakdown = React.useCallback(async (
    stageId: string,
    metricKey: string,
    snapshotData: L2CChainSnapshot,
    driverId?: string,
  ) => {
    const stage = snapshotData.stages.find((s) => s.id === stageId);
    if (!stage) return;

    const resolvedDriverId = driverId
      ?? stage.panel?.drivers?.find((d) => d.default)?.id
      ?? stage.breakdown_drivers?.find((d) => d.default)?.id
      ?? stage.breakdown_drivers?.[0]?.id
      ?? 'channel';

    const cacheKey = metricBreakdownCacheKey(stageId, metricKey, resolvedDriverId);
    metricBreakdownActiveKey.current = cacheKey;
    const sessionCached = metricBreakdownCache.current.get(cacheKey);
    if (
      sessionCached
      && !isStaleZeroConversionBreakdown(stage, metricKey, sessionCached.movers as L2CMover[])
    ) {
      setMetricBreakdown(sessionCached);
      setMetricBreakdownDriverId(sessionCached.driver_id ?? resolvedDriverId);
      return;
    }

    const panelCached = readPanelMetricBreakdown(stage, metricKey, resolvedDriverId);
    if (panelCached) {
      metricBreakdownCache.current.set(cacheKey, panelCached);
      setMetricBreakdown(panelCached);
      setMetricBreakdownDriverId(resolvedDriverId);
      return;
    }

    const payload = buildStageDetailPayload(stage, snapshotData);
    if (!payload) return;

    const metric = [...stage.headline_metrics, ...(stage.supporting_metrics ?? [])]
      .find((m) => m.key === metricKey) ?? null;

    setMetricBreakdownLoading(true);

    // Reuse an in-flight request for this exact metric+driver instead of firing a
    // second one — otherwise switching back to a metric while its first (often
    // slower, first-time-LLM) request is still pending would kick off a duplicate
    // generation, and whichever response happens to land last silently wins.
    let fetchPromise = metricBreakdownInFlight.current.get(cacheKey);
    if (!fetchPromise) {
      fetchPromise = fetchLeadToCashMetricBreakdown(
        stageId,
        metricKey,
        {
          grain: payload.grain,
          preset: payload.preset,
          current_period: payload.current_period,
          prior_period: payload.prior_period,
          comparison_mode: payload.comparison_mode,
          comparison_label: payload.comparison_label,
          metric,
        },
        resolvedDriverId,
      ).finally(() => {
        metricBreakdownInFlight.current.delete(cacheKey);
      });
      metricBreakdownInFlight.current.set(cacheKey, fetchPromise);
    }

    try {
      const result = await fetchPromise;
      metricBreakdownCache.current.set(cacheKey, result);
      if (metricBreakdownActiveKey.current !== cacheKey) return;
      setMetricBreakdown(result);
      setMetricBreakdownDriverId(result.driver_id ?? resolvedDriverId);
    } catch {
      if (metricBreakdownActiveKey.current !== cacheKey) return;
      setMetricBreakdown({
        stage_id: stageId,
        metric_key: metricKey,
        movers: [],
        breakdown_unavailable: true,
        error: 'Failed to load metric breakdown.',
      });
    } finally {
      if (metricBreakdownActiveKey.current === cacheKey) {
        setMetricBreakdownLoading(false);
      }
    }
  }, []);

  const loadStageDetail = React.useCallback(async (
    stageId: string,
    snapshotData: L2CChainSnapshot,
    options?: { driverId?: string; moversPage?: number; force?: boolean; narrativePoll?: boolean },
  ) => {
    const requestKey = options?.narrativePoll
      ? `${stageId}:narrative`
      : `${stageId}:${options?.driverId ?? 'default'}:${options?.moversPage ?? 1}`;
    if (detailInFlight.current === requestKey) return;

    const stage = snapshotData.stages.find((s) => s.id === stageId);
    if (!stage || stage.data_unavailable) return;
    if (
      !options?.force
      && !options?.driverId
      && !options?.moversPage
      && !options?.narrativePoll
      && stageHasPanel(stage)
    ) {
      if (stage.panel?.narrative_pending) {
        pollStageNarrative(stageId);
      }
      return;
    }

    const payload = buildStageDetailPayload(stage, snapshotData);
    if (!payload) return;

    // The driver toggle is set optimistically (see handleDriverChange) before this
    // fetch resolves. If the fetch fails or returns nothing, revert the selection so
    // the highlighted tab never disagrees with the (stale) data actually on screen.
    const previousDriverId = stage.panel?.active_driver ?? null;
    detailInFlight.current = requestKey;
    const isDriverSwitch = Boolean(options?.driverId);
    if (!options?.narrativePoll) {
      if (isDriverSwitch) {
        setBreakdownDriverLoading(true);
      } else {
        setDetailLoadingStageId(stageId);
      }
    }
    try {
      const detail = await fetchLeadToCashStageDetail(stageId, payload, {
        driverId: options?.driverId,
        moversPage: options?.moversPage ?? 1,
      });
      if (!detail.panel) {
        if (isDriverSwitch) setHeadlineBreakdownDriverId(previousDriverId);
        return;
      }
      const panel = detail.panel;
      setSnapshot((prev) => {
        if (!prev) return prev;
        return updateStagePanel(prev, stageId, panel, currencySymbol);
      });
      if (options?.driverId) {
        setHeadlineBreakdownDriverId(options.driverId);
      }
      if (detail.narrative_pending || panel.narrative_pending) {
        pollStageNarrative(stageId);
      } else {
        setNarrativeLoadingStageId((current) => (current === stageId ? null : current));
      }
    } catch {
      if (isDriverSwitch) setHeadlineBreakdownDriverId(previousDriverId);
    } finally {
      if (detailInFlight.current === requestKey) {
        detailInFlight.current = null;
      }
      if (!options?.narrativePoll) {
        if (isDriverSwitch) {
          setBreakdownDriverLoading(false);
        } else {
          setDetailLoadingStageId((current) => (current === stageId ? null : current));
        }
      }
    }
  }, [pollStageNarrative, currencySymbol]);

  React.useEffect(() => () => clearNarrativePoll(), [clearNarrativePoll]);

  const load = React.useCallback(async (
    range: CustomRange | null,
    rerun = false,
  ) => {
    if (!leadToCashFeature.enabled) return;

    const generation = ++loadGeneration.current;
    const hadSnapshot = snapshotRef.current != null;
    detailInFlight.current = null;
    metricBreakdownCache.current.clear();
    metricBreakdownActiveKey.current = null;
    clearNarrativePoll();
    setDetailLoadingStageId(null);
    setNarrativeLoadingStageId(null);
    setLoading(true);
    if (hadSnapshot) setRefreshing(true);
    setError(null);
    try {
      const query = rangeToQuery(range, rerun);
      const data = rerun
        ? await rerunLeadToCashChain(query)
        : await fetchLeadToCashChain(query);
      if (generation !== loadGeneration.current) return;
      applySnapshot(data);
    } catch (e) {
      if (generation !== loadGeneration.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load chain');
      if (!hadSnapshot) setSnapshot(null);
    } finally {
      if (generation !== loadGeneration.current) return;
      setLoading(false);
      setRefreshing(false);
    }
  }, [applySnapshot, clearNarrativePoll, leadToCashFeature.enabled]);

  React.useEffect(() => {
    if (!leadToCashFeature.enabled) {
      setSnapshot(null);
      setLoading(false);
      setError(null);
      return;
    }
    void load(custom, false);
  }, [custom, leadToCashFeature.enabled, load]);

  const selectedStage: L2CStage | null = React.useMemo(() => {
    if (!snapshot || !selectedStageId) return null;
    return snapshot.stages.find((s) => s.id === selectedStageId) ?? null;
  }, [snapshot, selectedStageId]);

  const snapshotMatchesQuery =
    Boolean(snapshot && custom && snapshotMatchesRange(snapshot, custom));

  const activeGrain: L2CPeriodGrain = snapshot?.period_grain ?? 'week';

  const stages = snapshotMatchesQuery ? (snapshot?.stages ?? []) : [];
  const comparisonMode = snapshot?.comparison_mode ?? 'wow';

  const isInitialLoad = loading && !snapshot;
  const isUpdating = loading && !!snapshot && leadToCashFeature.enabled;
  const isApplyingNewView = isUpdating && !snapshotMatchesQuery;
  const showViewSkeleton = isInitialLoad;

  const pendingCompare = custom ? resolveDialOpComparePeriod({ ...custom, compareEnabled: true }) : null;
  const displayCurrentPeriod =
    isApplyingNewView && custom
      ? { start: custom.currentStart, end: custom.currentEnd }
      : (snapshot?.current_period ?? snapshot?.current_week);
  const displayPriorPeriod =
    isApplyingNewView && custom
      ? pendingCompare
        ? { start: pendingCompare.start, end: pendingCompare.end }
        : null
      : (snapshot?.prior_period ?? snapshot?.prior_week);

  usePageBanner(
    isUpdating
      ? {
          message: isApplyingNewView
            ? 'Updating data for your selected period…'
            : 'Refreshing data from the warehouse…',
          tone: 'info',
        }
      : null,
  );

  const getSurfaceContext = React.useCallback(
    () => surfaceContextRef.current,
    [],
  );

  const { sendMessage, sendSQLApproval, postContextNote, stopStream } = useLeadToCashChat(
    getSurfaceContext,
    chatOpen && isDesktop,
  );

  React.useEffect(() => {
    if (!snapshotMatchesQuery || !snapshot) {
      surfaceContextRef.current = null;
      return;
    }
    surfaceContextRef.current = buildL2CSurfaceContext({
      snapshot,
      grain: activeGrain,
      preset: snapshot.comparison_preset ?? 'custom',
      selectedStageId,
    });
  }, [snapshot, activeGrain, selectedStageId, snapshotMatchesQuery]);

  const handlePeriodApply = React.useCallback((range: CustomRange) => {
    setCustom(range);
    if (chatOpen) {
      postContextNote(`View changed to ${range.currentStart} – ${range.currentEnd}.`);
    }
  }, [chatOpen, postContextNote]);

  const handleSelectStage = React.useCallback((stageId: string) => {
    clearNarrativePoll();
    setNarrativeLoadingStageId(null);
    setActiveBreakdownMetricKey(null);
    metricBreakdownActiveKey.current = null;
    setMetricBreakdown(null);
    setMetricBreakdownDriverId(null);
    setHeadlineBreakdownDriverId(null);
    setSelectedStageId(stageId);
    setDetailRetryNonce((n) => n + 1);
    try {
      sessionStorage.setItem(L2C_SELECTED_STAGE_KEY, stageId);
    } catch {
      // sessionStorage unavailable
    }
  }, [clearNarrativePoll]);

  const handleSelectBreakdownMetric = React.useCallback((metricKey: string | null) => {
    const stage = snapshot?.stages.find((s) => s.id === selectedStageId);
    const primaryKey = stage?.primary_metric_key ?? stage?.headline_metrics[0]?.key;
    const resolvedKey = metricKey && metricKey !== primaryKey ? metricKey : null;

    setActiveBreakdownMetricKey(resolvedKey);
    if (!resolvedKey) {
      metricBreakdownActiveKey.current = null;
      setMetricBreakdown(null);
      setMetricBreakdownDriverId(null);
      setMetricBreakdownLoading(false);
      return;
    }
    const defaultDriver = stage?.panel?.drivers?.find((d) => d.default)?.id
      ?? stage?.breakdown_drivers?.find((d) => d.default)?.id
      ?? stage?.breakdown_drivers?.[0]?.id
      ?? 'channel';
    setMetricBreakdownDriverId(defaultDriver);
    if (!snapshot || !selectedStageId) {
      setMetricBreakdownLoading(true);
      return;
    }

    const cacheKey = metricBreakdownCacheKey(selectedStageId, resolvedKey, defaultDriver);
    // Mark this as the selection the user cares about *before* checking caches —
    // a still-pending request for whatever metric was active before must not be
    // able to clobber this one when it eventually resolves.
    metricBreakdownActiveKey.current = cacheKey;
    const sessionCached = metricBreakdownCache.current.get(cacheKey);
    const panelCached = readPanelMetricBreakdown(stage, resolvedKey, defaultDriver);
    const cached = sessionCached
      && !isStaleZeroConversionBreakdown(stage, resolvedKey, sessionCached.movers as L2CMover[])
      ? sessionCached
      : panelCached;
    if (cached) {
      setMetricBreakdown(cached);
      setMetricBreakdownLoading(false);
      if (panelCached && !sessionCached) {
        metricBreakdownCache.current.set(cacheKey, panelCached);
      }
      return;
    }
    setMetricBreakdown(null);
    setMetricBreakdownLoading(true);
    void loadMetricBreakdown(selectedStageId, resolvedKey, snapshot, defaultDriver);
  }, [loadMetricBreakdown, selectedStageId, snapshot]);

  const handleMetricDriverChange = React.useCallback((driverId: string) => {
    if (!snapshot || !selectedStageId || !activeBreakdownMetricKey) return;
    setMetricBreakdownDriverId(driverId);
    void loadMetricBreakdown(selectedStageId, activeBreakdownMetricKey, snapshot, driverId);
  }, [activeBreakdownMetricKey, loadMetricBreakdown, selectedStageId, snapshot]);

  const handleDriverChange = React.useCallback((driverId: string) => {
    if (!snapshot || !selectedStageId) return;
    setHeadlineBreakdownDriverId(driverId);

    const stage = snapshot.stages.find((s) => s.id === selectedStageId);
    if (stage?.panel && panelHasBreakdownStore(stage.panel) && stage.panel.breakdowns?.[driverId]) {
      const isCurrency = isStagePrimaryMetricCurrency(stage);
      setSnapshot((prev) => {
        if (!prev) return prev;
        const current = prev.stages.find((s) => s.id === selectedStageId);
        if (!current?.panel) return prev;
        return updateStagePanel(
          prev,
          selectedStageId,
          paginateStagePanel(current.panel, { driverId, page: 1, isCurrency, currencySymbol }),
          currencySymbol,
        );
      });
      return;
    }
    void loadStageDetail(selectedStageId, snapshot, {
      driverId,
      moversPage: 1,
      force: true,
    });
  }, [snapshot, selectedStageId, loadStageDetail, currencySymbol]);

  const handleOpenStageChat = React.useCallback((stageId: string) => {
    handleSelectStage(stageId);
    if (!chatOpen && isDesktop) {
      setChatOpen(true);
    }
    const stage = snapshot?.stages.find((s) => s.id === stageId);
    if (stage && chatOpen) {
      postContextNote(`Let's discuss the ${stage.name} stage.`);
    } else if (stage) {
      window.setTimeout(() => {
        postContextNote(`Let's discuss the ${stage.name} stage.`);
      }, 300);
    }
  }, [chatOpen, handleSelectStage, isDesktop, postContextNote, snapshot?.stages]);

  React.useEffect(() => {
    if (
      !leadToCashFeature.enabled
      || loading
      || !snapshotMatchesQuery
      || !selectedStageId
      || !snapshot
    ) {
      return;
    }

    const stage = snapshot.stages.find((s) => s.id === selectedStageId);
    if (!stage || stage.data_unavailable || stageHasPanel(stage)) return;

    void loadStageDetail(selectedStageId, snapshot);
  }, [
    leadToCashFeature.enabled,
    loading,
    snapshotMatchesQuery,
    selectedStageId,
    snapshot?.computed_at,
    loadStageDetail,
    snapshot,
    detailRetryNonce,
  ]);

  const splitChat = chatOpen && isDesktop;

  React.useEffect(() => {
    window.dispatchEvent(new CustomEvent('klaris:l2c-chat-split', { detail: { active: splitChat } }));
    return () => {
      window.dispatchEvent(new CustomEvent('klaris:l2c-chat-split', { detail: { active: false } }));
    };
  }, [splitChat]);

  if (!leadToCashFeature.loading && !leadToCashFeature.enabled) {
    return (
      <Alert severity="info">
        Lead-to-Cash Monitor is not enabled for this workspace. Enable it under{' '}
        <strong>Settings → Features</strong>.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: splitChat ? 'flex' : 'block',
        flex: splitChat ? 1 : undefined,
        minHeight: splitChat ? 0 : undefined,
        height: splitChat ? '100%' : undefined,
        alignItems: splitChat ? 'stretch' : undefined,
        width: '100%',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, minHeight: splitChat ? 0 : undefined, overflow: splitChat ? 'auto' : undefined }}>
        <Box sx={{ maxWidth: splitChat ? 'none' : 1680, mx: splitChat ? 0 : 'auto' }}>
      <LeadToCashToolbar
        period={custom}
        snapshot={snapshot}
        loading={loading}
        refreshing={refreshing}
        disabled={loading}
        onPeriodApply={handlePeriodApply}
        onRerun={() => void load(custom, true)}
        chatOpen={chatOpen}
        onToggleChat={() => setChatOpen((v) => !v)}
        showChatToggle={isDesktop}
      />

      <DialOpPeriodContext
        loading={showViewSkeleton}
        currentPeriod={displayCurrentPeriod}
        priorPeriod={displayPriorPeriod}
        compareEnabled
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {snapshotMatchesQuery && snapshot?.partial_message && !showViewSkeleton && (
        <Alert severity={snapshot?.partial_data ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
          {snapshot.partial_message}
        </Alert>
      )}

      {!snapshot?.data_available && snapshot?.message && !loading && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {snapshot.message}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={(t) => ({
          borderRadius: `${t.ds?.radius?.md ?? 8}px`,
          overflow: 'hidden',
          mb: 2,
          opacity: isUpdating ? 0.55 : 1,
          pointerEvents: isUpdating ? 'none' : 'auto',
          transition: 'opacity 0.2s ease',
        })}
      >
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            {!showViewSkeleton && snapshot?.capacity && snapshotMatchesQuery && snapshot.data_available && (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <CapacityInline
                  icon={<PhoneInTalkRounded sx={{ fontSize: 13 }} />}
                  label="Talk"
                  current={snapshot.capacity.talk_hours}
                  prior={snapshot.capacity.prior_talk_hours}
                  suffix="h"
                />
                <CapacityInline
                  icon={<GroupsRounded sx={{ fontSize: 13 }} />}
                  label="Agents"
                  current={snapshot.capacity.active_agents}
                  prior={snapshot.capacity.prior_active_agents}
                />
                <CapacityInline
                  icon={<PhoneInTalkRounded sx={{ fontSize: 13 }} />}
                  label="Hrs/AA/BD"
                  current={snapshot.capacity.hours_per_aa_bd}
                  prior={snapshot.capacity.prior_hours_per_aa_bd}
                  suffix="h"
                />
              </Stack>
            )}

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center" sx={{ ml: 'auto' }}>
              {!showViewSkeleton && snapshotMatchesQuery && stages.length > 0 && (
                <ChainHealthSummary stages={stages} />
              )}
              {showViewSkeleton && (
                <>
                  <Skeleton variant="rounded" width={68} height={22} animation="wave" sx={{ borderRadius: 3 }} />
                  <Skeleton variant="rounded" width={64} height={22} animation="wave" sx={{ borderRadius: 3 }} />
                  <Skeleton variant="rounded" width={72} height={22} animation="wave" sx={{ borderRadius: 3 }} />
                </>
              )}
              {snapshotMatchesQuery && snapshot?.period_in_progress && !showViewSkeleton && (
                <Chip size="small" label="Period in progress" color="info" variant="outlined" />
              )}
              {snapshotMatchesQuery && snapshot?.cohort_immature && !showViewSkeleton && (
                <Chip size="small" label="Cohort maturing" color="warning" variant="outlined" />
              )}
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          {showViewSkeleton ? (
            <ChainSkeleton />
          ) : (
            <ChainView
              stages={stages}
              selectedStageId={selectedStageId}
              onSelectStage={handleSelectStage}
              comparisonLabel={snapshot?.comparison_label}
              periodGrain={activeGrain}
            />
          )}
        </Box>

        {!showViewSkeleton && snapshot?.chain_summary && snapshotMatchesQuery && (
          <ExecutiveSummary summary={snapshot.chain_summary} />
        )}
      </Paper>

      {showViewSkeleton ? (
        <StageDetailSkeleton />
      ) : (
        snapshotMatchesQuery && (
          <Box sx={{ opacity: isUpdating ? 0.55 : 1, pointerEvents: isUpdating ? 'none' : 'auto', transition: 'opacity 0.2s ease' }}>
            <StageDetailPanel
              stage={selectedStage}
              comparisonMode={comparisonMode}
              comparisonLabel={snapshot?.comparison_label}
              periodGrain={activeGrain}
              currentPeriodStart={displayCurrentPeriod?.start}
              currentPeriodEnd={displayCurrentPeriod?.end}
              priorPeriodStart={displayPriorPeriod?.start}
              priorPeriodEnd={displayPriorPeriod?.end}
              detailLoading={detailLoadingStageId === selectedStageId}
              narrativeLoading={narrativeLoadingStageId === selectedStageId}
              onDriverChange={handleDriverChange}
              onOpenChat={selectedStage ? () => handleOpenStageChat(selectedStage.id) : undefined}
              headlineBreakdownDriverId={headlineBreakdownDriverId}
              activeBreakdownMetricKey={activeBreakdownMetricKey}
              onSelectBreakdownMetric={handleSelectBreakdownMetric}
              breakdownDriverLoading={breakdownDriverLoading}
              metricBreakdownLoading={metricBreakdownLoading}
              metricBreakdownMovers={(metricBreakdown?.movers ?? []) as L2CMover[]}
              metricBreakdownDrivers={(metricBreakdown?.drivers ?? []) as L2CDriver[]}
              metricBreakdownActiveDriver={metricBreakdownDriverId}
              metricBreakdownUnavailable={Boolean(metricBreakdown?.breakdown_unavailable)}
              onMetricDriverChange={handleMetricDriverChange}
              metricSummary={metricBreakdown?.metric_summary ?? null}
            />
          </Box>
        )
      )}
        </Box>
      </Box>

      {chatOpen && isDesktop && (
        <>
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat panel"
            title="Drag to resize · double-click to reset"
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            onDoubleClick={onResizeDoubleClick}
            sx={(t) => ({
              flexShrink: 0,
              width: 6,
              cursor: 'col-resize',
              position: 'relative',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.18 : 0.1),
              },
            })}
          />
          <Box
            sx={{
              width: chatWidth,
              flexShrink: 0,
              minHeight: 0,
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <LeadToCashChatPanel
              width={chatWidth}
              onSend={sendMessage}
              onStop={stopStream}
              onSQLApproval={sendSQLApproval}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

function CapacityInline({
  icon,
  label,
  current,
  prior,
  suffix = '',
}: {
  icon: React.ReactNode;
  label: string;
  current?: number | null;
  prior?: number | null;
  suffix?: string;
}) {
  const fmt = (v: number | null | undefined) => formatL2CValueWithSuffix(v, suffix);
  const cur = fmt(current);
  const pri = fmt(prior);

  return (
    <Stack direction="row" spacing={0.4} alignItems="center" color="text.secondary">
      {icon}
      <Typography variant="caption">
        {label} {cur}
        <Typography component="span" variant="caption" color="text.disabled">
          {' '}
          (was {pri})
        </Typography>
      </Typography>
    </Stack>
  );
}
